// Admin/NotificationSendingComponent.jsx
import React, { useState, useEffect } from "react";
import { Bell, Users, Building2, Send, AlertCircle } from "lucide-react";

const NotificationSendingComponent = () => {
  const [notificationData, setNotificationData] = useState({
    title: "",
    body: "",
    target: "all", // 'all', 'zetacoms', 'zetaflow'
    tenantId: "", // Optional, for specific tenant targeting
  });

  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [showTenantSelect, setShowTenantSelect] = useState(false);

  // Fetch tenants using the correct endpoint
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_MAIN_API_Url}/api/Tenant/get-tenant-info`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setTenants(data);
        } else {
          console.error("Failed to fetch tenants");
        }
      } catch (error) {
        console.error("Error fetching tenants:", error);
      }
    };

    fetchTenants();
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setSendStatus(null);

    try {
      // Determine the endpoint and prepare the request body
      let endpoint;
      let requestBody;

      if (notificationData.tenantId) {
        endpoint = `Broadcast/tenant/${notificationData.tenantId}`;
        requestBody = {
          title: notificationData.title,
          body: notificationData.body,
        };
      } else if (notificationData.target === "all") {
        endpoint = "Broadcast/all";
        requestBody = {
          title: notificationData.title,
          body: notificationData.body,
        };
      } else {
        endpoint = "Broadcast/app";
        requestBody = {
          title: notificationData.title,
          body: notificationData.body,
          appType:
            notificationData.target === "zetacoms" ? "ZetaComs" : "ZetaFlow",
        };
      }

      console.log("Sending request to:", endpoint);
      console.log("Request body:", requestBody);

      const response = await fetch(
        `${process.env.REACT_APP_MAIN_API_Url}/api/${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        // Get the error details from the response
        const errorText = await response.text();
        throw new Error(`Failed to send broadcast notification: ${errorText}`);
      }

      const result = await response.json();

      setSendStatus({
        type: "success",
        message: `Successfully sent to ${result.successCount} devices${
          result.failureCount ? ` (${result.failureCount} failed)` : ""
        }`,
      });

      // Reset form
      setNotificationData({
        ...notificationData,
        title: "",
        body: "",
        tenantId: "",
      });
    } catch (error) {
      console.error("Error details:", error);
      setSendStatus({
        type: "error",
        message: error.message || "Failed to send notification",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleTargetChange = (target) => {
    setNotificationData((prev) => ({
      ...prev,
      target,
      tenantId: "", // Reset tenantId when changing target
    }));
    setShowTenantSelect(target === "zetaflow");
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-[#0E0444] mb-2">
          Send Broadcast Notification
        </h2>
        <p className="text-[#4C4D5E]">
          Send notifications to your users across different platforms
        </p>
      </div>

      <form onSubmit={handleSend} className="space-y-6">
        {/* Target Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => handleTargetChange("all")}
            className={`p-4 rounded-lg border-2 ${
              notificationData.target === "all"
                ? "border-[#0C60F2] bg-blue-50"
                : "border-gray-200 hover:border-gray-300"
            } transition-colors`}
          >
            <Users className="w-6 h-6 mb-2 text-[#0C60F2]" />
            <p className="font-medium text-[#0E0444]">All Users</p>
            <p className="text-sm text-[#4C4D5E]">Both ZetaComs and ZetaFlow</p>
          </button>

          <button
            type="button"
            onClick={() => handleTargetChange("zetacoms")}
            className={`p-4 rounded-lg border-2 ${
              notificationData.target === "zetacoms"
                ? "border-[#0C60F2] bg-blue-50"
                : "border-gray-200 hover:border-gray-300"
            } transition-colors`}
          >
            <Bell className="w-6 h-6 mb-2 text-[#0C60F2]" />
            <p className="font-medium text-[#0E0444]">ZetaComs Users</p>
            <p className="text-sm text-[#4C4D5E]">Landing page subscribers</p>
          </button>

          <button
            type="button"
            onClick={() => handleTargetChange("zetaflow")}
            className={`p-4 rounded-lg border-2 ${
              notificationData.target === "zetaflow"
                ? "border-[#0C60F2] bg-blue-50"
                : "border-gray-200 hover:border-gray-300"
            } transition-colors`}
          >
            <Building2 className="w-6 h-6 mb-2 text-[#0C60F2]" />
            <p className="font-medium text-[#0E0444]">ZetaFlow Users</p>
            <p className="text-sm text-[#4C4D5E]">Application users</p>
          </button>
        </div>

        {/* Tenant Selection for ZetaFlow */}
        {showTenantSelect && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-[#4C4D5E] mb-2">
              Select Company (Optional)
            </label>
            <select
              value={notificationData.tenantId}
              onChange={(e) =>
                setNotificationData((prev) => ({
                  ...prev,
                  tenantId: e.target.value,
                }))
              }
              className="w-full px-4 py-2 rounded-md border border-gray-300 focus:border-[#0C60F2] focus:ring-1 focus:ring-[#0C60F2]"
            >
              <option value="">All Companies</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} {tenant.domain ? `(${tenant.domain})` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Notification Content */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#4C4D5E] mb-2">
              Notification Title
            </label>
            <input
              type="text"
              value={notificationData.title}
              onChange={(e) =>
                setNotificationData((prev) => ({
                  ...prev,
                  title: e.target.value,
                }))
              }
              required
              className="w-full px-4 py-2 rounded-md border border-gray-300 focus:border-[#0C60F2] focus:ring-1 focus:ring-[#0C60F2]"
              placeholder="Enter notification title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#4C4D5E] mb-2">
              Message
            </label>
            <textarea
              value={notificationData.body}
              onChange={(e) =>
                setNotificationData((prev) => ({
                  ...prev,
                  body: e.target.value,
                }))
              }
              required
              rows="4"
              className="w-full px-4 py-2 rounded-md border border-gray-300 focus:border-[#0C60F2] focus:ring-1 focus:ring-[#0C60F2] resize-none"
              placeholder="Enter notification message"
            />
          </div>
        </div>

        {/* Send Button */}
        <button
          type="submit"
          disabled={isSending}
          className={`w-full bg-[#0C60F2] text-white py-3 rounded-md font-semibold flex items-center justify-center
            ${
              isSending ? "opacity-70 cursor-not-allowed" : "hover:bg-[#0A4ED1]"
            }
            transition-all duration-200`}
        >
          {isSending ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Sending...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Send Notification
            </>
          )}
        </button>

        {/* Status Message */}
        {sendStatus && (
          <div
            className={`mt-4 p-4 rounded-md flex items-center ${
              sendStatus.type === "success"
                ? "bg-green-50 text-green-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            <AlertCircle className="w-5 h-5 mr-2" />
            <p className="text-sm font-medium">{sendStatus.message}</p>
          </div>
        )}
      </form>
    </div>
  );
};

export default NotificationSendingComponent;
